import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "react-oidc-context";
import { useLocalStorage } from "usehooks-ts";

import { DEFAULT_BASE_CURRENCY } from "@/constants/accounts";
import { BOOKING_ENTITY } from "@/constants/config";
import { useMixpanelIdentify } from "@/hooks/useMixpanelIdentify";
import { isBootPrefetchEnabled, writeBootHint } from "@/lib/bootHint";
import { resolveAccountAccess } from "@/utils/accountStateAccess";
import { useGetSelfQuery } from "@generated/redux/services/generated/api";
import { CustomerAccountUser } from "@generated/types";

import {
  deriveBookingEntityFlags,
  type ExtendedSelf,
  extendProfile,
  selectDefaultAccount,
} from "./CustomerAccountProvider.utils";

type CustomerAccountContextValue = {
  customerAccountId: string | null;
  customerAccount: CustomerAccountUser | null;
  customerAccounts: CustomerAccountUser[] | null;
  changeCustomerAccount: (
    customerAccountId: string | null,
    accountName?: string
  ) => void;
  setIsSwitchingAccount: (isSwitchingAccount: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isSwitchingAccount: boolean;
  switchingAccountName: string | null;
  userProfile: ExtendedSelf | null;
  customerAccountBaseCurrency: string;
  /** SUSPENDED account: informational surfaces only, all write flows removed. */
  isAccountSuspended: boolean;
  hasCurrencies: boolean;
  hasDigitalAssets: boolean;
  hasBothEntities: boolean;
  /** Re-run the /self fetch (used by the splash boot-error retry). */
  refetchSelf: () => Promise<unknown>;
};

const CustomerAccountContext = createContext<CustomerAccountContextValue>({
  customerAccountId: null,
  customerAccount: null,
  customerAccounts: null,
  changeCustomerAccount: () => {},
  isLoading: false,
  isError: false,
  isSuccess: false,
  setIsSwitchingAccount: () => {},
  isSwitchingAccount: false,
  switchingAccountName: null,
  userProfile: null,
  customerAccountBaseCurrency: DEFAULT_BASE_CURRENCY,
  isAccountSuspended: false,
  hasCurrencies: false,
  hasDigitalAssets: false,
  hasBothEntities: false,
  refetchSelf: () => Promise.resolve(),
});

const ACCOUNT_SWITCH_DELAY = 750;
const EMPTY_ACCOUNTS: CustomerAccountUser[] = [];

const CustomerAccountProvider = ({ children }: WithChildren) => {
  const [customerAccountId, setCustomerAccountId] = useLocalStorage<
    string | null
  >("customerAccountId", null);
  const {
    data: userProfile,
    isLoading,
    isSuccess,
    isError,
    refetch: refetchSelf,
  } = useGetSelfQuery();

  // Read the context directly rather than `useAuth()`, which warns when there
  // is no AuthProvider (Storybook, unit tests): no auth means no hint.
  const oidcSub = useContext(AuthContext)?.user?.profile?.sub;

  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [switchingAccountName, setSwitchingAccountName] = useState<
    string | null
  >(null);

  // Stabilize empty array reference when userProfile is null/loading
  const customerAccounts = userProfile?.customerAccounts ?? EMPTY_ACCOUNTS;

  // Cancel any pending switch timeout on unmount or rapid re-switch
  const switchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (switchTimeoutRef.current) {
        clearTimeout(switchTimeoutRef.current);
      }
    };
  }, []);

  const changeCustomerAccount = useCallback(
    (newCustomerAccountId: string | null, accountName?: string) => {
      if (newCustomerAccountId === customerAccountId) {
        // Reset switching state if trying to switch to the same account
        setIsSwitchingAccount(false);
        setSwitchingAccountName(null);
        return;
      }

      // Cancel any pending timeout from a previous switch
      if (switchTimeoutRef.current) {
        clearTimeout(switchTimeoutRef.current);
      }

      setSwitchingAccountName(accountName || null);
      setCustomerAccountId(newCustomerAccountId);

      switchTimeoutRef.current = setTimeout(() => {
        switchTimeoutRef.current = null;
        setIsSwitchingAccount(false);
        setSwitchingAccountName(null);
      }, ACCOUNT_SWITCH_DELAY);
    },
    [customerAccountId, setCustomerAccountId]
  );

  const customerAccount =
    customerAccounts.find((account) => account.id === customerAccountId) ||
    null;

  // Get the customer's base currency with fallback to default
  const customerAccountBaseCurrency =
    customerAccount?.customerAccount?.profile?.baseCurrency ||
    DEFAULT_BASE_CURRENCY;

  const isAccountSuspended =
    resolveAccountAccess(customerAccount?.customerAccount?.accountState) ===
    "viewOnly";

  // Derive booking entity flags from customerAccount
  const { hasCurrencies, hasDigitalAssets, hasBothEntities } = useMemo(
    () => deriveBookingEntityFlags(customerAccount, BOOKING_ENTITY),
    [customerAccount]
  );

  // Memoize extended profile to avoid creating new object references each render
  const completeUserProfile = useMemo<ExtendedSelf | null>(() => {
    if (!userProfile) return null;
    return {
      ...extendProfile(userProfile),
      customerAccounts: customerAccounts as CustomerAccountUser[],
    } as ExtendedSelf;
  }, [userProfile, customerAccounts]);

  // Remember the account in view for the next boot's prefetch
  // (`useBootPrefetch`, run by OnboardingRouter while /self is pending).
  // Re-runs on an account switch, so the hint always names the last account
  // viewed.
  const resolvedCustomerAccountUuid = customerAccount?.customerAccount?.id;
  useEffect(() => {
    if (!oidcSub || !resolvedCustomerAccountUuid) return;
    if (!isBootPrefetchEnabled()) return;
    writeBootHint({
      sub: oidcSub,
      customerAccountUuid: resolvedCustomerAccountUuid,
    });
  }, [oidcSub, resolvedCustomerAccountUuid]);

  // Identify user in Mixpanel when profile is loaded
  useMixpanelIdentify({
    userProfile: completeUserProfile,
    customerAccountId: customerAccount?.customerAccount?.id || null,
    isSuccess,
  });

  /**
   * Ensures customerAccountId in localStorage is valid: if missing or not
   * found in the user's accounts, it will be set to a default account.
   *
   * Deliberately NOT latched to run once per mount: the selected id can stop
   * resolving mid-session (a /self refetch that drops an account, or an
   * external state reset such as Storybook's StateResetDecorator), and a
   * dangling id would leave the app account-less. Re-selecting is loop-safe —
   * selectDefaultAccount always returns an id from the list, so the next run
   * resolves and no-ops.
   */
  useEffect(() => {
    if (!isSuccess || isError) {
      return;
    }
    if (customerAccounts.length) {
      const currentCustomerAccount = customerAccountId
        ? customerAccounts.find((account) => account.id === customerAccountId)
        : null;

      if (!currentCustomerAccount) {
        const defaultId = selectDefaultAccount(
          customerAccounts as CustomerAccountUser[]
        );
        queueMicrotask(() => changeCustomerAccount(defaultId));
      }
    }
  }, [
    isSuccess,
    isError,
    customerAccounts,
    customerAccountId,
    changeCustomerAccount,
  ]);

  const value = useMemo<CustomerAccountContextValue>(
    () => ({
      customerAccountId,
      customerAccount: customerAccount as CustomerAccountUser | null,
      customerAccounts: customerAccounts.length
        ? (customerAccounts as CustomerAccountUser[])
        : null,
      changeCustomerAccount,
      setIsSwitchingAccount,
      isLoading,
      isError,
      isSuccess,
      isSwitchingAccount,
      switchingAccountName,
      userProfile: completeUserProfile,
      customerAccountBaseCurrency,
      isAccountSuspended,
      hasCurrencies,
      hasDigitalAssets,
      hasBothEntities,
      refetchSelf,
    }),
    [
      customerAccountId,
      customerAccount,
      customerAccounts,
      changeCustomerAccount,
      isLoading,
      isError,
      isSuccess,
      isSwitchingAccount,
      switchingAccountName,
      completeUserProfile,
      customerAccountBaseCurrency,
      isAccountSuspended,
      hasCurrencies,
      hasDigitalAssets,
      hasBothEntities,
      refetchSelf,
    ]
  );

  if (isLoading) return null;

  return (
    <CustomerAccountContext.Provider value={value}>
      {children}
    </CustomerAccountContext.Provider>
  );
};

export default CustomerAccountProvider;

export const useCustomerAccount = () => useContext(CustomerAccountContext);
